/**
 * <s-require-choice>
 *
 * Blocks add to cart and express checkout until the shopper has picked a value for every
 * product option, so nobody orders the wrong fitment by accident.
 *
 * Contract:
 *   - Lives inside the product <form>. Only active when it has [data-required].
 *   - Option groups to check are elements with [data-require-choice="<message>"] inside the form.
 *     A group counts as chosen when it has a checked radio or a <select> with a value.
 *   - [data-require-choice-error] inside this element receives the message.
 *   - Express checkout buttons placed inside this element are intercepted on click (capture phase).
 *
 * The form's submit listener runs at the target (the form) before <s-product-form>'s bubbling
 * listener, so stopImmediatePropagation keeps the item out of the cart.
 */
class SRequireChoice extends HTMLElement {
  #ac = null;

  connectedCallback() {
    if (!this.hasAttribute('data-required')) return;
    const form = this.closest('form');
    if (!form) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    form.addEventListener('submit', (event) => {
      if (this.#check(form)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, opts);

    // Express checkout (Shop Pay, Apple Pay, etc.) clicks.
    this.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('[data-require-choice-express]')) return;
      if (this.#check(form)) return;
      event.preventDefault();
      event.stopPropagation();
    }, { ...opts, capture: true });

    form.addEventListener('change', () => this.#clearError(form), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #check(form) {
    const groups = form.querySelectorAll('[data-require-choice]');
    for (const group of groups) {
      const chosen = group.querySelector('input[type="radio"]:checked')
        || [...group.querySelectorAll('select')].some((select) => select.value);
      if (!chosen) {
        this.#showError(group);
        return false;
      }
    }
    return true;
  }

  #showError(group) {
    const error = this.querySelector('[data-require-choice-error]');
    if (error) {
      error.textContent = group.dataset.requireChoice || '';
      error.hidden = false;
    }
    group.setAttribute('data-invalid', '');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    group.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    const firstControl = group.querySelector('input:not([disabled]), select');
    firstControl?.focus({ preventScroll: true });
  }

  #clearError(form) {
    const error = this.querySelector('[data-require-choice-error]');
    if (error) error.hidden = true;
    form.querySelectorAll('[data-require-choice][data-invalid]').forEach((group) => group.removeAttribute('data-invalid'));
  }
}

if (!customElements.get('s-require-choice')) {
  customElements.define('s-require-choice', SRequireChoice);
}
